import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ComparativeRating } from '@/lib/authorAnalyticsUtils';

interface ComparativeStatsProps {
  comparativeData: ComparativeRating[];
}

export function ComparativeStats({ comparativeData }: ComparativeStatsProps) {
  if (comparativeData.length === 0) return null;

  const lovedMore = comparativeData.filter(b => b.agreementType === 'loved_more');
  const lovedLess = comparativeData.filter(b => b.agreementType === 'loved_less');
  const aligned = comparativeData.filter(b => b.agreementType === 'aligned');

  const avgDifference = comparativeData.reduce((sum, b) => sum + Math.abs(b.difference), 0) / comparativeData.length;
  const agreementRate = Math.round((aligned.length / comparativeData.length) * 100);

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Your Taste vs. Community
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          How your ratings compare to the community average
        </p>

        {/* Overall Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-primary">{agreementRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">Agreement Rate</p>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-accent">{lovedMore.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Loved More</p>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-secondary">{lovedLess.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Loved Less</p>
          </div>
        </div>

        {/* Taste Analysis */}
        <div className="p-4 bg-muted/20 rounded-lg text-center">
          <p className="text-sm">
            {agreementRate >= 60 ? (
              <>You tend to <span className="font-bold text-primary">align closely</span> with community ratings</>
            ) : agreementRate <= 40 ? (
              <>You have a <span className="font-bold text-primary">unique perspective</span> - often rating differently than the crowd</>
            ) : (
              <>You have a <span className="font-bold text-primary">balanced</span> mix of agreement and unique opinions</>
            )}
          </p>
        </div>

        {/* Biggest Differences - Loved More */}
        {lovedMore.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium">Hidden Gems You Discovered</p>
            </div>
            <div className="space-y-3">
              {lovedMore.slice(0, 3).map((book) => (
                <div key={book.bookId} className="space-y-1">
                  <p className="font-medium text-sm">{book.title}</p>
                  <p className="text-xs text-muted-foreground">{book.authors.join(', ')}</p>
                  <div className="flex items-center gap-3">
                    <Badge variant="default" className="text-xs">
                      You: ⭐ {book.userRating}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      Community: ⭐ {book.communityRating.toFixed(1)}
                    </Badge>
                    <span className="text-xs text-primary font-medium">
                      +{book.difference.toFixed(1)} higher
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Biggest Differences - Loved Less */}
        {lovedLess.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-accent" />
              <p className="text-sm font-medium">Popular Books You Weren't Into</p>
            </div>
            <div className="space-y-3">
              {lovedLess.slice(0, 3).map((book) => (
                <div key={book.bookId} className="space-y-1">
                  <p className="font-medium text-sm">{book.title}</p>
                  <p className="text-xs text-muted-foreground">{book.authors.join(', ')}</p>
                  <div className="flex items-center gap-3">
                    <Badge variant="default" className="text-xs">
                      You: ⭐ {book.userRating}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      Community: ⭐ {book.communityRating.toFixed(1)}
                    </Badge>
                    <span className="text-xs text-accent font-medium">
                      {book.difference.toFixed(1)} lower
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Perfect Alignments */}
        {aligned.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Minus className="w-4 h-4 text-secondary" />
              <p className="text-sm font-medium">Books Where You Aligned</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {aligned.slice(0, 5).map((book) => (
                <Badge key={book.bookId} variant="secondary" className="text-xs">
                  {book.title} (⭐ {book.userRating})
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
